// Cookie consent (GET-115). Optional tools (analytics, ad pixels, chat) load only through `onConsent`, so none of them
// can run before the visitor accepts. `OPTIONAL_COOKIES` lists the kinds in use, never the products, so swapping a
// provider leaves the Cookie policy as it is: the policy shows the list, and the banner asks only while it isn't
// empty, so nobody is asked to consent to nothing. A new kind adds its row here; its tool loads from
// `onConsent(() => …)`.
import { computed, ref } from 'vue'

export interface OptionalCookie {
  kind: string
  purpose: string
}

// Empty until analytics or ad pixels ship (launch ticket 6). Rows as worded for the Cookie policy:
//   { kind: 'Analytics', purpose: 'help us understand how the site is used, so we can improve it.' },
//   { kind: 'Advertising', purpose: 'help us measure which of our adverts bring visitors.' },
export const OPTIONAL_COOKIES: readonly OptionalCookie[] = []

export type ConsentChoice = 'accepted' | 'rejected'

const STORAGE_KEY = 'cookie-consent'
// A choice is asked again after a year, as the UK regulator (ICO) suggests.
const MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000

interface Stored {
  choice: ConsentChoice
  at: number
}

function readStored(): ConsentChoice | null {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Stored | null
    if (stored && (stored.choice === 'accepted' || stored.choice === 'rejected') && Date.now() - stored.at < MAX_AGE_MS) {
      return stored.choice
    }
  } catch {
    // Storage blocked or a corrupt value: ask again, which is the safe default.
  }
  return null
}

const choice = ref<ConsentChoice | null>(readStored())
const reopened = ref(false)
const waiting: Array<() => void> = []

export const consentChoice = computed(() => choice.value)

export const bannerVisible = computed(
  () => OPTIONAL_COOKIES.length > 0 && (choice.value === null || reopened.value),
)

export function hasConsent(): boolean {
  return choice.value === 'accepted'
}

// Runs `load` now if the visitor has accepted, else once they do. Never runs after a rejection.
export function onConsent(load: () => void): void {
  if (hasConsent()) load()
  else waiting.push(load)
}

export function setConsent(next: ConsentChoice): void {
  const withdrawn = choice.value === 'accepted' && next === 'rejected'
  choice.value = next
  reopened.value = false
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ choice: next, at: Date.now() } satisfies Stored))
  } catch {
    // Storage blocked: the choice holds for this page view, and the banner asks again next time.
  }
  if (next === 'accepted') waiting.splice(0).forEach((load) => load())
  // A loaded tool can't be unloaded: a reload starts the page without it.
  if (withdrawn) window.location.reload()
}

// "Cookie settings": shows the banner again so a choice can be changed as easily as it was made.
export function reopenConsent(): void {
  reopened.value = true
}
